import { Link } from "react-router-dom";
import { ArrowRight, Scale, Zap, Users } from "lucide-react";
import { BrandImage } from "@/components/Brand";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { LineReveal } from "@/components/LineReveal";

const VALUES = [
  { icon: Zap, title: "Speed over theatre", text: "A fast, honest answer beats a slow, polished one. We optimise for the moment you're actually in." },
  { icon: Users, title: "Humans over process", text: "Processes serve people, never the reverse. If a policy makes your business worse, the policy loses." },
  { icon: Scale, title: "Candour over comfort", text: "If you're about to do something risky, we say so plainly. That's the job." },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <Seo
        title="About Electra HR, Solicitor-Founded HR for UK SMEs"
        siteName="Electra HR"
        description="Electra HR was founded by Vanessa Challess, a practising UK litigation solicitor, to give small businesses honest HR advice at prices they can justify."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin },
            { "@type": "ListItem", position: 2, name: "About", item: `${window.location.origin}/about` },
          ],
        }}
      />
      <section className="grain bg-ink pb-20 pt-32 sm:pt-40 text-paper">
        <div className="container-x">
          <p className="eyebrow text-pink">About</p>
          <LineReveal
            className="mt-6 max-w-4xl font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]"
            lines={["Built by someone who got", "tired of corporate HR."]}
            delay={0.1}
          />
          <p className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-paper/65">
            Electra HR exists because too many founders were paying retainers to firms that answered
            slowly, spoke in jargon, and treated every question as a billing opportunity. We're the opposite of that.
          </p>
        </div>
      </section>

      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow text-ink/50">The story</p>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                A solicitor who chose the HR side of the table
              </h2>
              <div className="mt-6 space-y-5 max-w-2xl text-base leading-relaxed text-ink/70">
                <p>
                  Electra HR was founded by Vanessa Challess, a practising litigation solicitor who
                  spent years watching the same pattern: a small
                  business hits a people problem, calls a corporate HR firm or a law firm, and gets either a
                  script or an invoice. Rarely an answer.
                </p>
                <p>
                  So Electra was built around what SMEs actually need: fast, plain-English HR advice from
                  someone with genuine legal judgement, at prices a 10-person company can justify. Based in
                  Rochester, Kent; working with founders across the UK.
                </p>
                <p>
                  And because some matters genuinely do turn legal, Electra works alongside Bonsai Law, a
                  separate, SRA-regulated law firm, so the moment a situation needs legal advice,
                  representation or privilege protection, you're handed straight in with full context.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="clip-frame overflow-hidden">
                <BrandImage
                  src="/images/vanessa-challess-480.webp"
                  srcSet="/images/vanessa-challess-480.webp 480w, /images/vanessa-challess-900.webp 900w"
                  sizes="(min-width: 1024px) 40vw, 100vw"
                  fallback="/images/vanessa-challess.jpg"
                  alt="Vanessa Challess, founder of Electra HR"
                  className="h-[380px] w-full object-cover"
                />
              </div>
              <p className="mt-4 font-mono text-xs text-ink/40">
                Rochester, Kent is our home base. Our clients are everywhere from Cornwall to the Highlands.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="team-section" className="bg-ink py-16 sm:py-24 text-paper">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow text-pink">The people</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
              Small team. Real humans. No call handlers.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl">
            <Reveal>
              <article data-testid="team-card-vanessa" className="h-full border border-white/10 border-t-[3px] border-t-bolt bg-ink-soft">
                <img
                  src="/images/vanessa-challess-900.webp"
                  srcSet="/images/vanessa-challess-480.webp 480w, /images/vanessa-challess-900.webp 900w"
                  sizes="(min-width: 768px) 45vw, 100vw"
                  alt="Vanessa Challess, founder of Electra HR"
                  loading="lazy"
                  className="h-72 w-full object-cover object-top"
                />
                <div className="p-8">
                  <p className="font-display text-xl font-bold text-bolt">Vanessa Challess</p>
                  <p className="mt-1 text-sm text-paper/55">Founder · practising litigation solicitor</p>
                  <p className="mt-4 text-sm leading-relaxed text-paper/65">
                    The one who got tired of watching founders pay law-firm rates for HR answers.
                    Vanessa leads every piece of advice and decides the moment a matter needs
                    Bonsai Law's regulated lawyers.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                    <a href="mailto:vanessa@electra-hr.com" data-testid="team-vanessa-email" className="font-medium text-pink hover:text-bolt transition-colors">vanessa@electra-hr.com</a>
                    <a href="tel:+441622957300" data-testid="team-vanessa-phone" className="font-medium text-pink hover:text-bolt transition-colors">01622 957 300</a>
                  </div>
                </div>
              </article>
            </Reveal>
            <Reveal delay={0.1}>
              <article data-testid="team-card-isaline" className="h-full border border-white/10 border-t-[3px] border-t-bolt bg-ink-soft">
                <img
                  src="/images/isaline-sergeant-480.webp"
                  srcSet="/images/isaline-sergeant-480.webp 480w, /images/isaline-sergeant-900.webp 900w"
                  sizes="(min-width: 768px) 45vw, 100vw"
                  alt="Isaline Sergeant, Senior HR Consultant at Electra HR"
                  loading="lazy"
                  className="h-72 w-full object-cover object-top"
                />
                <div className="p-8">
                  <p className="font-display text-xl font-bold text-bolt">Isaline Sergeant</p>
                  <p className="mt-1 text-sm text-paper/55">Senior HR Consultant</p>
                  <p className="mt-4 text-sm leading-relaxed text-paper/65">
                    The steady hand on day-to-day HR: advice, processes and the conversations
                    managers dread. Isaline keeps things calm, fair and moving.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                    <a href="mailto:isaline@electra-hr.com" data-testid="team-isaline-email" className="font-medium text-pink hover:text-bolt transition-colors">isaline@electra-hr.com</a>
                    <a href="tel:+441622957300" data-testid="team-isaline-phone" className="font-medium text-pink hover:text-bolt transition-colors">01622 957 300</a>
                  </div>
                </div>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-y border-ink/10 bg-paper pb-16 sm:pb-24">
        <div className="container-x">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              ["/images/Electra-HR-full-800.webp", "Electra HR brand mark on navy"],
              ["/images/ELectra-lightning-800.webp", "The Electra HR lightning bolt"],
              ["/images/Electra-2-800.webp", "Electra HR logo on white"],
            ].map(([src, alt]) => (
              <Reveal key={src}>
                <div className="clip-frame overflow-hidden">
                  <img src={src} alt={alt} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-700 hover:scale-105" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink py-16 sm:py-24 text-paper">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow text-pink">How we work</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">Three rules we won't break</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div data-testid={`about-value-${i}`} className="h-full border border-white/10 bg-ink-soft p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-pink/60">
                  <v.icon size={26} className="text-pink" strokeWidth={1.8} />
                  <h3 className="mt-5 font-display text-xl font-semibold">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-paper/60">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-14 border border-white/10 p-8 sm:p-10">
              <p className="eyebrow text-pink">The Electra / Bonsai distinction</p>
              <p className="mt-4 max-w-3xl text-sm sm:text-base leading-relaxed text-paper/65">
                Electra HR is an HR consultancy, not a law firm, and is not regulated by the Solicitors
                Regulation Authority. Day-to-day HR, including advice, documents, processes and training, sits with us.
                Anything that needs legal advice, representation or the protection of legal professional
                privilege is provided separately by Bonsai Law, an SRA-regulated firm. One joined-up route,
                two clear roles, no grey areas.
              </p>
              <Link to="/contact" data-testid="about-cta-contact" className="group mt-7 inline-flex items-center gap-2 bg-pink px-6 py-3.5 font-bold text-ink transition-transform duration-300 hover:-translate-y-0.5">
                Work with us <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
